// src/pages/FormSummary.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useWizardFormId, withForm, formBelongsToCompany } from '../utils/wizardForm';

function FormSummary() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Номер формы — из адреса этой вкладки (см. utils/wizardForm.js)
  const formId = useWizardFormId();

  // loading
  const [loading, setLoading] = useState(true);

  // Company display/edit
  const [company, setCompany] = useState(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({});
  const [companySaving, setCompanySaving] = useState(false);
  const [companyError, setCompanyError] = useState('');

  // TaxYear display/edit
  const [taxYear, setTaxYear] = useState('');
  const [editingTaxYear, setEditingTaxYear] = useState(false);
  const [taxYearForm, setTaxYearForm] = useState('');
  const [savingTaxYear, setSavingTaxYear] = useState(false);
  const [taxYearError, setTaxYearError] = useState('');
  const [firstUsedMonth, setFirstUsedMonth] = useState('');
  const [firstUsedMonthForm, setFirstUsedMonthForm] = useState('');

  // Form2290 summary
  const [vehicles, setVehicles] = useState([]);
  const [finalReturn, setFinalReturn] = useState(false);
  const [continueError, setContinueError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [editingPaymentInfo, setEditingPaymentInfo] = useState(false);
  const [paymentInfoForm, setPaymentInfoForm] = useState({
    routing_number: '',
    account_number: '',
    confirm_account: '',
    phone_number: '',
    account_type: 'Checking',
  });
  const [paymentInfoSaving, setPaymentInfoSaving] = useState(false);
  const [paymentInfoError, setPaymentInfoError] = useState('');
  const [totalIrsTax, setTotalIrsTax] = useState('');

  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment and IRS Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 4;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        // Load company
        const compRes = await fetch(`/api/companies/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!compRes.ok) throw new Error('Error loading company');
        const compData = await compRes.json();
        setCompany(compData);

        // Load form2290
        if (formId) {
          const formRes = await fetch(`/api/form2290/${formId}/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!formRes.ok) throw new Error('Error loading form2290');
          const data = await formRes.json();
          if (!formBelongsToCompany(data, id)) {
            navigate(`/company/${id}`, { replace: true });
            return;
          }
          setFinalReturn(Boolean(data.final_return));

          // Tax period
          setTaxYear(data.tax_period || '');
          setTaxYearForm(data.tax_period || '');

          // First used month
          setFirstUsedMonth(data.first_used_month || '');
          setFirstUsedMonthForm(data.first_used_month || '');


          // Payment method
          setPaymentMethod(data.irs_payment_option || '');

          // Payment info
          if (data.payment_info) {
            try {
              const info = JSON.parse(data.payment_info);
              setPaymentInfo(info);
              setPaymentInfoForm({
                routing_number: info.routing_number || '',
                account_number: info.account_number || '',
                confirm_account: info.account_number || '',
                phone_number: info.phone_number || '',
                account_type: info.account_type || 'Checking',
              });
            } catch {
              // ignore parse errors
            }
          }

          // Vehicles
          const details = await Promise.all(
            (data.vehicles || []).map(async (vid) => {
              const rv = await fetch(`/api/vehicles/${vid}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!rv.ok) throw new Error(`Error loading vehicle ${vid}`);
              return rv.json();
            })
          );
          setVehicles(details);

          // Compute total IRS tax
          const sumTax = details.reduce(
            (acc, v) => acc + (parseFloat(v.taxable_weight) || 0),
            0
          );
          setTotalIrsTax(`$${sumTax.toFixed(2)}`);

          // Persist step and total_tax_due
          await fetch(`/api/form2290/${formId}/`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ total_tax_due: sumTax, step: 4 }),
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, formId, navigate]);

  // Company edit handlers
  const handleEditCompany = () => {
    setCompanyForm(company);
    setCompanyError('');
    setEditingCompany(true);
  };
  const handleCancelCompany = () => setEditingCompany(false);
  const handleCompanyChange = e => {
    const { name, value, type, checked } = e.target;
    setCompanyForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };
  const handleSaveCompany = async () => {
    setCompanyError('');
    setCompanySaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/companies/${id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(companyForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || 'Error saving company');
      }
      const updated = await res.json();
      setCompany(updated);
      setEditingCompany(false);
    } catch (e) {
      setCompanyError(e.message);
    } finally {
      setCompanySaving(false);
    }
  };

  // TaxYear edit handlers
  const handleEditTaxYear = () => {
    setTaxYearError('');
    setEditingTaxYear(true);
  };
  // Обработчики для first_used_month
  const handleFirstUsedMonthChange = e => {
    setFirstUsedMonthForm(e.target.value);
  };

  const handleCancelTaxYear = () => {
    setTaxYearForm(taxYear);
    setFirstUsedMonthForm(firstUsedMonth);
    setEditingTaxYear(false);
  };


  const handleTaxYearChange = e => {
    setTaxYearForm(e.target.value);
  };
  const handleSaveTaxYear = async () => {
    setTaxYearError('');
    setSavingTaxYear(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/form2290/${formId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tax_period: taxYearForm,
          first_used_month: firstUsedMonthForm
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || 'Error saving tax period');
      }
      setFirstUsedMonth(firstUsedMonthForm);
      setTaxYear(taxYearForm);
      setEditingTaxYear(false);
    } catch (e) {
      setTaxYearError(e.message);
    } finally {
      setSavingTaxYear(false);
    }
  };

  // Payment Info edit handlers
  const handleEditPaymentInfo = () => {
    setPaymentInfoError('');
    setEditingPaymentInfo(true);
  };
  const handleCancelPaymentInfo = () => {
    // reset form to last saved
    setPaymentInfoForm(prev => ({
      ...prev,
      confirm_account: paymentInfo?.account_number || ''
    }));
    setEditingPaymentInfo(false);
  };
  const handlePaymentInfoChange = e => {
    const { name, value } = e.target;
    setPaymentInfoForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSavePaymentInfo = async () => {
    setPaymentInfoError('');
    setPaymentInfoSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        routing_number: paymentInfoForm.routing_number,
        account_number: paymentInfoForm.account_number,
        phone_number: paymentInfoForm.phone_number,
        account_type: paymentInfoForm.account_type,
      };
      const res = await fetch(`/api/form2290/${formId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ payment_info: JSON.stringify(payload) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || 'Error saving payment info');
      }
      setPaymentInfo(payload);
      setPaymentInfoForm(prev => ({
        ...prev,
        confirm_account: payload.account_number
      }));
      setEditingPaymentInfo(false);
    } catch (e) {
      setPaymentInfoError(e.message);
    } finally {
      setPaymentInfoSaving(false);
    }
  };

  const handleUpdateTotal = () => {
    // IRS отклоняет нефинальную декларацию без машин (F2290-027-01), а сервер
    // не даст её оплатить — говорим об этом здесь, а не на странице оплаты.
    if (vehicles.length === 0 && !finalReturn) {
      setContinueError(
        'Add at least one vehicle before filing. The IRS rejects a Form 2290 that lists no vehicles.'
      );
      return;
    }
    setContinueError('');
    navigate(withForm(`/company/${id}/payment-and-submission`, formId));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderLogged />

      {/* Progress bar */}
      <div className="bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap justify-center gap-3">
          {steps.map(step => {
            const isCurrent = step.id === currentStep;
            const isNext    = step.id === currentStep + 1;
            const isCompleted = step.id < currentStep;

            const baseClasses = 'text-sm font-medium px-3 py-1 border';
            const visibleClasses = isCurrent || isNext || isCompleted
              ? 'block'
              : 'hidden sm:block';

            const colorClasses = isCurrent
              ? 'bg-[#0F2C5E] text-white border-[#0F2C5E]'
              : isCompleted
                ? 'bg-green-100 text-green-700 border-green-200'
                : isNext
                  ? 'bg-[#E4E8F0] text-[#0F2C5E] border-[#E4E8F0]'
                  : 'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div
                key={step.id}
                className={`${baseClasses} ${visibleClasses} ${colorClasses}`}
              >
                {step.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 bg-[#E4F2C5E] py-6 px-4">
        <div className="max-w-6xl mx-auto bg-white border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-[#0F2C5E]">
              Form Summary
            </h1>
          </div>

          <div className="p-6 space-y-6">
            {/* Vehicles table */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-[#0F2C5E]">
                  Taxable Vehicles
                </h2>
                <button
                  onClick={() => navigate(withForm(`/company/${id}/file-form/vehicles-tax`, formId))}
                  className="bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition"
                >
                  Edit Vehicles
                </button>
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="w-full border-collapse text-xs sm:text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="border px-3 py-2 text-left">S.No</th>
                      <th className="border px-3 py-2 text-left">VIN</th>
                      <th className="border px-3 py-2 text-left">Letter</th>
                      <th className="border px-3 py-2 text-left">Weight</th>
                      <th className="border px-3 py-2 text-left">Tax Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center px-3 py-6">
                          No vehicles added yet.
                        </td>
                      </tr>
                    ) : (
                      vehicles.map((v, idx) => (
                        <tr
                          key={v.vin}
                          className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        >
                          <td className="border px-3 py-2">{idx + 1}</td>
                          <td className="border px-3 py-2">{v.vin}</td>
                          <td className="border px-3 py-2">{v.gross_vehicle_label}</td>
                          <td className="border px-3 py-2">{v.gross_vehicle_weight}</td>
                          <td className="border px-3 py-2">${v.taxable_weight}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right column */}
            <div className="bg-gray-50 border border-gray-200 space-y-4 p-4">
              {/* Business Information */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h2 className="font-semibold text-gray-800">Business Information</h2>
                  {!editingCompany ? (
                    <button
                      onClick={handleEditCompany}
                      className="bg-[#0F2C5E] text-white px-3 py-1 text-xs hover:bg-[#0F2C5E]/80 transition"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="space-x-2">
                      <button
                        onClick={handleSaveCompany}
                        disabled={companySaving}
                        className="bg-green-600 text-white px-3 py-1 text-xs hover:bg-green-700 transition disabled:opacity-50"
                      >
                        {companySaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelCompany}
                        disabled={companySaving}
                        className="bg-gray-300 text-gray-700 px-3 py-1 text-xs hover:bg-gray-400 transition disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                {companyError && <p className="text-red-500 text-sm mb-2">{companyError}</p>}
                {!editingCompany ? (
                  <div className="text-sm text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                      {/* Left column: Company info */}
                      <div className="space-y-1">
                        <div>Name: {company.name}</div>
                        <div>EIN: {company.EIN}</div>
                        <div>Type: {company.business_type}</div>
                        <div>Street Address: {company.street_address}</div>
                        <div>City/State/ZIP: {company.city}, {company.state} {company.zip_code}</div>
                        <div>Country: {company.country}</div>
                      </div>

                      {/* Right column: Signing Authority */}
                      <div className="space-y-1 mt-4 md:mt-0">
                        <div className="font-medium">Signing Authority</div>
                        <div>Name: {company.signing_name}</div>
                        <div>Title: {company.signing_job_title}</div>
                        <div>Phone: {company.signing_phone}</div>
                        <div>Third Party Allowed: {company.third_party_allowed ? 'Yes' : 'No'}</div>
                      </div>
                    </div>
                ) : (
                  <form className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
                    <div>
                      <label className="block mb-1">Name</label>
                      <input
                        name="name"
                        value={companyForm.name}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">EIN</label>
                      <input
                        name="EIN"
                        value={companyForm.EIN}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Business Type</label>
                      <select
                        name="business_type"
                        value={companyForm.business_type}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      >
                        <option value="LLC">LLC</option>
                        <option value="Corporation">Corporation</option>
                        <option value="Sole Proprietor">Sole Proprietor</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1">Street Address</label>
                      <AddressAutocomplete
                        value={companyForm.street_address || ''}
                        onChange={handleCompanyChange}
                        onAddressSelected={({ streetAddress, city, state, zip }) => {
                          // Всегда перезаписываем выбранным значением, чтобы
                          // formatted_address из onChange не оставался в street.
                          setCompanyForm(prev => ({
                            ...prev,
                            street_address: streetAddress || '',
                            city: city || '',
                            state: state || '',
                            zip_code: zip || '',
                          }));
                        }}
                        inputProps={{
                          name: 'street_address',
                          className: 'w-full p-2 border border-gray-300',
                        }}
                      />
                    </div>
                    <div>
                      <label className="block mb-1">City</label>
                      <input
                        name="city"
                        value={companyForm.city}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">State</label>
                      <input
                        name="state"
                        value={companyForm.state}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">ZIP Code</label>
                      <input
                        name="zip_code"
                        value={companyForm.zip_code}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Country</label>
                      <input
                        name="country"
                        value={companyForm.country}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Signing Name</label>
                      <input
                        name="signing_name"
                        value={companyForm.signing_name}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Signing Title</label>
                      <input
                        name="signing_job_title"
                        value={companyForm.signing_job_title}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Signing Phone</label>
                      <input
                        name="signing_phone"
                        value={companyForm.signing_phone}
                        onChange={handleCompanyChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="third_party_allowed"
                        checked={companyForm.third_party_allowed}
                        onChange={handleCompanyChange}
                        className="h-4 w-4"
                      />
                      <label>Allow Third Party</label>
                    </div>
                  </form>
                )}
              </div>


              {/* Tax Year Information */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h2 className="font-semibold text-gray-800">Tax Year Information</h2>
                  {!editingTaxYear ? (
                    <button
                      onClick={handleEditTaxYear}
                      className="bg-[#0F2C5E] text-white px-3 py-1 text-xs hover:bg-[#0F2C5E]/80 transition"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="space-x-2">
                      <button
                        onClick={handleSaveTaxYear}
                        disabled={savingTaxYear}
                        className="bg-green-600 text-white px-3 py-1 text-xs hover:bg-green-700 transition disabled:opacity-50"
                      >
                        {savingTaxYear ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelTaxYear}
                        disabled={savingTaxYear}
                        className="bg-gray-300 text-gray-700 px-3 py-1 text-xs hover:bg-gray-400 transition disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {taxYearError && <p className="text-red-500 text-sm mb-2">{taxYearError}</p>}

                {!editingTaxYear ? (
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>Selected Period: {taxYear || 'N/A'}</div>
                    <div>First Used Month: {firstUsedMonth || 'N/A'}</div>
                  </div>
                ) : (
                  <div className="space-y-4 text-sm">
                    <div>
                      <label className="block mb-1">Selected Period</label>
                      <select
                        value={taxYearForm}
                        onChange={handleTaxYearChange}
                        className="w-full p-2 border border-gray-300"
                      >
                        <option value="2024-2025">2024-2025</option>
                        <option value="2025-2026">2025-2026</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1">First Used Month</label>
                      <select
                        value={firstUsedMonthForm}
                        onChange={handleFirstUsedMonthChange}
                        className="w-full p-2 border border-gray-300"
                      >
                        {[
                          'July','August','September','October','November','December',
                          'January','February','March','April','May','June'
                        ].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>

                    </div>
                  </div>
                )}
              </div>


              {/* Payment Method */}
              <div>
                <h2 className="font-semibold text-gray-800 mb-2">Payment Method</h2>
                <div className="text-sm text-gray-700">{paymentMethod || 'N/A'}</div>
              </div>

              {/* ← New Payment Info block */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h2 className="font-semibold text-gray-800">Payment Info</h2>
                  {!editingPaymentInfo ? (
                    <button
                      onClick={handleEditPaymentInfo}
                      className="bg-[#0F2C5E] text-white px-3 py-1 text-xs hover:bg-[#0F2C5E]/80 transition"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="space-x-2">
                      <button
                        onClick={handleSavePaymentInfo}
                        disabled={paymentInfoSaving}
                        className="bg-green-600 text-white px-3 py-1 text-xs hover:bg-green-700 transition disabled:opacity-50"
                      >
                        {paymentInfoSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelPaymentInfo}
                        disabled={paymentInfoSaving}
                        className="bg-gray-300 text-gray-700 px-3 py-1 text-xs hover:bg-gray-400 transition disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                {paymentInfoError && (
                  <p className="text-red-500 text-sm mb-2">{paymentInfoError}</p>
                )}
                {!editingPaymentInfo ? paymentInfo ? (
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>Routing: {paymentInfo.routing_number}</div>
                    <div>Account: {paymentInfo.account_number}</div>
                    <div>Phone: {paymentInfo.phone_number}</div>
                    <div>Type: {paymentInfo.account_type}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No payment info set.</div>
                ) : (
                  <form className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
                    <div>
                      <label className="block mb-1">Routing Number</label>
                      <input
                        name="routing_number"
                        value={paymentInfoForm.routing_number}
                        onChange={handlePaymentInfoChange}
                        maxLength={9}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Account Number</label>
                      <input
                        name="account_number"
                        value={paymentInfoForm.account_number}
                        onChange={handlePaymentInfoChange}
                        maxLength={17}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Confirm Account </label>
                      <input
                        name="confirm_account"
                        value={paymentInfoForm.confirm_account}
                        onChange={handlePaymentInfoChange}
                        maxLength={17}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Phone Number</label>
                      <input
                        name="phone_number"
                        value={paymentInfoForm.phone_number}
                        onChange={handlePaymentInfoChange}
                        className="w-full p-2 border border-gray-300"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block mb-1">Account Type</label>
                      <select
                        name="account_type"
                        value={paymentInfoForm.account_type}
                        onChange={handlePaymentInfoChange}
                        className="w-full p-2 border border-gray-300"
                      >
                        <option value="Checking">Checking</option>
                        <option value="Savings">Savings</option>
                      </select>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {continueError && (
              <p className="text-red-600 text-sm mt-4" role="alert">{continueError}</p>
            )}

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row justify-between mt-4 gap-4">
              <button
                onClick={() => navigate(withForm(`/company/${id}/file-form/irs-payment-options`, formId))}
                className="bg-gray-300 text-gray-700 px-4 py-2 hover:bg-gray-400 transition w-full sm:w-auto"
              >
                Back
              </button>
              <button
                onClick={handleUpdateTotal}
                className="bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition w-full sm:w-auto"
              >
                Continue Filing
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default FormSummary;
